import { Camera, ChevronRight } from "lucide-react";
import { Fragment, type ReactNode } from "react";

import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { InstalacionDeInforme } from "@/lib/informes";
import { formatearCifra, formatearMonto } from "@/lib/montos";
import { useResaltadoDeSincronizaciones } from "@/hooks/useResaltadoDeSincronizaciones";
import { cn } from "@/lib/utils";

import { conteoDeRegistrosDelItem } from "./aguasTerreno";
import { estaSinValorizar } from "./cuadroDeInstalaciones";
import { FilaDeTerrenoDeLaFila } from "./FilaDeTerrenoDeLaFila";
import { marcaDeFila } from "./MarcasDeSincronizacion";
import {
  claveDeInstalacion,
  claveDeTerreno,
  creadaEnTerrenoSinValorizar,
  itemDeLaInstalacion,
  opcionesParaVincular,
  type ItemDeInstalacionPorEnlazar,
  type TerrenoDeLaLista,
} from "./instalacionesTerreno";
import { pluralizar } from "./registroDeTerreno";

interface TablaDeInstalacionesProps {
  filas: InstalacionDeInforme[];
  /** TODAS las guardadas, no solo las visibles: "Es la misma que" no depende del filtro. */
  todas?: InstalacionDeInforme[];
  ocupada: boolean;
  onAbrir: (fila: InstalacionDeInforme) => void;
  onValidar: (fila: InstalacionDeInforme, validada: boolean) => void;
  /** Lo que el encargado dejó en el predio; sin él la tabla es la de siempre. */
  terreno?: TerrenoDeLaLista;
  /** Qué registros de terreno están desplegados, por clave de fila. */
  abiertas?: ReadonlySet<string>;
  onAlternar?: (clave: string) => void;
  /** Completar una instalación de terreno: abre el formulario de alta precargado. */
  onAdoptar?: (porEnlazar: ItemDeInstalacionPorEnlazar) => void;
}

function Cifra({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cn("font-mono text-xs font-semibold", className)}>{children}</span>;
}

/**
 * La tabla de la lista: una fila por instalación u obra, hecha con el `Table`
 * del DS.
 *
 * LA FILA ENTERA ABRE EL MODAL, y el tipo es además un botón: un `onClick` en
 * un `<tr>` no lo alcanza el teclado. La casilla Valid. frena la propagación
 * —validar no puede abrir el detalle—.
 *
 * SIN COLUMNA DE SELECCIÓN, al revés que construcciones, y es el diseño: acá
 * quitar se hace desde el modal de cada fila y no en bloque.
 *
 * LAS CUATRO COLUMNAS DE VALOR DICEN UF EN SU ENCABEZADO, así que las celdas
 * van desnudas: repetir el símbolo en cada una es ruido.
 */
export function TablaDeInstalaciones({
  filas,
  todas = filas,
  ocupada,
  onAbrir,
  onValidar,
  terreno,
  abiertas,
  onAlternar,
  onAdoptar,
}: TablaDeInstalacionesProps) {
  const alinear = "text-right";
  const opciones = opcionesParaVincular(todas);
  const resaltado = useResaltadoDeSincronizaciones();

  return (
    <Table>
      <TableHeader>
        <TableRow className="hover:bg-transparent">
          <TableHead variant="versalitas" className="pl-5">Instalación u obra</TableHead>
          <TableHead variant="versalitas" className={alinear}>Año</TableHead>
          <TableHead variant="versalitas" className={alinear}>Cantidad</TableHead>
          <TableHead variant="versalitas" className={alinear}>UF/un. rep.</TableHead>
          <TableHead variant="versalitas" className={alinear}>Total rep. UF</TableHead>
          <TableHead variant="versalitas" className={alinear}>UF/un. com.</TableHead>
          <TableHead variant="versalitas" className={alinear}>Total com. UF</TableHead>
          <TableHead variant="versalitas">Fotos</TableHead>
          <TableHead variant="versalitas" className="text-center">Valid.</TableHead>
          <TableHead className="w-7 pr-3.5">
            <span className="sr-only">Abrir</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {filas.map((f) => {
          const fotos = f.fotos?.length ?? 0;
          const clave = claveDeInstalacion(f.id);
          const marca = marcaDeFila(resaltado.deItem(terreno ? itemDeLaInstalacion(terreno.items, f) : null));

          return (
            <Fragment key={f.id}>
            <TableRow
              id={clave}
              onClick={() => onAbrir(f)}
              {...marca.atributo}
              className={cn("h-10 cursor-pointer", marca.claseFila)}
            >
              <TableCell className={cn("max-w-[20rem] pl-5", marca.claseCelda)}>
                <div className="flex min-w-0 items-center gap-2">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onAbrir(f);
                  }}
                  className="text-ink focus-visible:ring-ring/50 block max-w-full min-w-0 truncate rounded-sm text-[0.78125rem] font-bold outline-none focus-visible:ring-[3px]"
                >
                  {f.tipo}
                </button>
                {marca.tag}
              </div>
              </TableCell>

              <TableCell className={alinear}>
                <Cifra>{f.anio ?? "—"}</Cifra>
              </TableCell>
              <TableCell className={alinear}>
                <Cifra className={cn("font-bold", f.cantidad === null && "text-ink-ghost")}>
                  {formatearCifra(f.cantidad)}
                </Cifra>
              </TableCell>
              <TableCell className={alinear}>
                <Cifra className={f.valor_reposicion_uf_un === null ? "text-ink-ghost" : undefined}>
                  {formatearCifra(f.valor_reposicion_uf_un)}
                </Cifra>
              </TableCell>
              <TableCell className={alinear}>
                <Cifra className={f.total_reposicion_uf === null ? "text-ink-ghost" : "font-bold"}>
                  {formatearMonto(f.total_reposicion_uf, null)}
                </Cifra>
              </TableCell>
              <TableCell className={alinear}>
                <Cifra className={f.valor_comercial_uf_un === null ? "text-ink-ghost" : undefined}>
                  {formatearCifra(f.valor_comercial_uf_un)}
                </Cifra>
              </TableCell>
              <TableCell className={alinear}>
                {estaSinValorizar(f) ? (
                  <span className="bg-warning-soft text-warning-soft-foreground rounded-sm px-1.5 py-0.5 text-[0.65625rem] font-bold">
                    Sin valorizar
                  </span>
                ) : (
                  <span
                    className={cn(
                      "font-mono text-[0.78125rem] font-bold",
                      f.total_comercial_uf === null && "text-ink-ghost",
                    )}
                  >
                    {formatearMonto(f.total_comercial_uf, null)}
                  </span>
                )}
              </TableCell>

              <TableCell>
                <span
                  aria-label={pluralizar(fotos, "foto", "fotos")}
                  className={cn(
                    "inline-flex items-center gap-1",
                    fotos === 0 ? "text-ink-ghost" : "text-ink-muted",
                  )}
                >
                  <Camera className="size-3" aria-hidden="true" />
                  <span className="font-mono text-[0.6875rem] font-bold">{fotos}</span>
                </span>
              </TableCell>

              <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  aria-label={`Validar ${f.tipo}`}
                  checked={f.validada}
                  disabled={ocupada}
                  title={f.validada ? "Quitar validación" : "Marcar los datos como revisados"}
                  // SE GUARDA SOLA y viaja sin el resto de los campos, para no
                  // pisar lo que otro pudo haber corregido mientras tanto.
                  onCheckedChange={(v) => onValidar(f, v === true)}
                />
              </TableCell>

              <TableCell className="pr-3.5 text-right">
                <ChevronRight className="text-ink-ghost inline size-3.5" aria-hidden="true" />
              </TableCell>
            </TableRow>
            {terreno ? (
              <FilaDeTerrenoDeLaFila
                terreno={terreno}
                titulo={f.tipo}
                item={itemDeLaInstalacion(terreno.items, f)}
                deTerreno={f.terreno_ref !== null}
                opciones={opciones}
                placeholderDelVinculo="Elegir instalación…"
                abierto={abiertas?.has(clave) ?? false}
                ocupada={ocupada}
                onAlternar={() => onAlternar?.(clave)}
                guardada={{
                  id: f.id,
                  fotos: f.fotos?.length ?? 0,
                  ausenciaMantenidaEn: f.ausencia_mantenida_en ?? null,
                }}
                queEs="instalación"
                esta="esta"
                creadaSinValorizar={creadaEnTerrenoSinValorizar(f)}
              />
            ) : null}
            </Fragment>
          );
        })}

        {terreno?.porEnlazar.map((porEnlazar) => {
          const { uuid, item } = porEnlazar;
          const clave = claveDeTerreno(uuid);
          const marca = marcaDeFila(resaltado.deItem(item));
          const fotos = conteoDeRegistrosDelItem(item).fotos;
          const sinDato = (valor: number | null | undefined) => (valor == null ? null : String(valor));

          return (
            <Fragment key={uuid}>
              <TableRow id={clave} onClick={() => onAdoptar?.(porEnlazar)} {...marca.atributo}
              className={cn("h-10 cursor-pointer", marca.claseFila)}>
                <TableCell className={cn("max-w-[20rem] pl-5", marca.claseCelda)}>
                  <div className="flex min-w-0 items-center gap-2">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onAdoptar?.(porEnlazar);
                    }}
                    className="text-ink focus-visible:ring-ring/50 block max-w-full min-w-0 truncate rounded-sm text-[0.78125rem] font-bold outline-none focus-visible:ring-[3px]"
                  >
                    {item.nombre}
                  </button>
                  {marca.tag}
                </div>
                </TableCell>
                <TableCell className={alinear}>
                  <Cifra className={item.anio == null ? "text-ink-ghost" : undefined}>{item.anio ?? "—"}</Cifra>
                </TableCell>
                <TableCell className={alinear}>
                  <Cifra className={cn("font-bold", item.cantidad == null && "text-ink-ghost")}>
                    {formatearCifra(sinDato(item.cantidad))}
                  </Cifra>
                </TableCell>
                <TableCell className={alinear}>
                  <Cifra className={item.valor_reposicion_uf_un == null ? "text-ink-ghost" : undefined}>
                    {formatearCifra(sinDato(item.valor_reposicion_uf_un))}
                  </Cifra>
                </TableCell>
                <TableCell className={alinear}>
                  <Cifra className="text-ink-ghost">—</Cifra>
                </TableCell>
                <TableCell className={alinear}>
                  <Cifra className={item.valor_comercial_uf_un == null ? "text-ink-ghost" : undefined}>
                    {formatearCifra(sinDato(item.valor_comercial_uf_un))}
                  </Cifra>
                </TableCell>
                <TableCell className={alinear}>
                  <span className="bg-warning-soft text-warning-soft-foreground rounded-sm px-1.5 py-0.5 text-[0.65625rem] font-bold">
                    Sin valorizar
                  </span>
                </TableCell>
                <TableCell>
                  <span
                    aria-label={pluralizar(fotos, "foto", "fotos")}
                    className={cn("inline-flex items-center gap-1", fotos === 0 ? "text-ink-ghost" : "text-ink-muted")}
                  >
                    <Camera className="size-3" aria-hidden="true" />
                    <span className="font-mono text-[0.6875rem] font-bold">{fotos}</span>
                  </span>
                </TableCell>
                <TableCell />
                <TableCell className="pr-3.5 text-right">
                  <ChevronRight className="text-ink-ghost inline size-3.5" aria-hidden="true" />
                </TableCell>
              </TableRow>
              <FilaDeTerrenoDeLaFila
                terreno={terreno}
                titulo={item.nombre}
                item={item}
                deTerreno
                uuidPorEnlazar={uuid}
                creadaSinValorizar
                opciones={opciones}
                placeholderDelVinculo="Elegir instalación…"
                abierto={abiertas?.has(clave) ?? false}
                ocupada={ocupada}
                onAlternar={() => onAlternar?.(clave)}
              />
            </Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
}
